import React, { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Smart Photo Selection
// ============================================================================

const PANEL_KEYWORDS: Record<string, string[]> = {
  modern: [
    'interior', 'apartment', 'unit', 'living', 'kitchen', 'bedroom',
    'home', 'modern', 'luxury', 'inside', 'hallway', 'dining',
  ],
  amenities: [
    'pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'amenities',
    'recreation', 'courtyard', 'grill', 'spa', 'theater', 'rooftop',
  ],
  neighborhood: [
    'aerial', 'drone', 'skyline', 'neighborhood', 'exterior', 'bird',
    'view', 'surrounding', 'city', 'street', 'building', 'location',
  ],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  panelKey: string,
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = PANEL_KEYWORDS[panelKey] || [];
  if (!gallery.length || !keywords.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  if (bestIndex >= 0) return bestIndex;

  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }

  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartPhotoIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const idx1 = findBestPhotoIndex(gallery, 'modern', used, 1);
    used.add(idx1);
    const idx2 = findBestPhotoIndex(gallery, 'amenities', used, 3);
    used.add(idx2);
    const idx3 = findBestPhotoIndex(gallery, 'neighborhood', used, 5);
    return [idx1, idx2, idx3];
  }, [gallery]);
}

function makeImageSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const panel1ImageDef = {
  label: 'Tab 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const panel2ImageDef = {
  label: 'Tab 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const panel3ImageDef = {
  label: 'Tab 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const panel1CtaDef = {
  label: 'Tab 1 CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Browse Photos' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel2CtaDef = {
  label: 'Tab 2 CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel3CtaDef = {
  label: 'Tab 3 CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'See What\'s Nearby' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

type TabId = 'panel1' | 'panel2' | 'panel3';

const TAB_INDEX: Record<TabId, number> = { panel1: 0, panel2: 1, panel3: 2 };
const TAB_COUNT = 3;

export function TabbedCallouts() {
  const [activeTab, setActiveTab] = useState<TabId>('panel1');
  const activeIndex = TAB_INDEX[activeTab];

  const [idx1, idx2, idx3] = useSmartPhotoIndices();

  const imgDef1 = useMemo(() => makeImageSlotDef('Tab 1 Image', idx1), [idx1]);
  const imgDef2 = useMemo(() => makeImageSlotDef('Tab 2 Image', idx2), [idx2]);
  const imgDef3 = useMemo(() => makeImageSlotDef('Tab 3 Image', idx3), [idx3]);

  const img1 = useSlotGroup('tabbedcallouts.panel1_image', imgDef1);
  const img2 = useSlotGroup('tabbedcallouts.panel2_image', imgDef2);
  const img3 = useSlotGroup('tabbedcallouts.panel3_image', imgDef3);

  const cta1 = useSlotGroup('tabbedcallouts.panel1_cta', panel1CtaDef);
  const cta2 = useSlotGroup('tabbedcallouts.panel2_cta', panel2CtaDef);
  const cta3 = useSlotGroup('tabbedcallouts.panel3_cta', panel3CtaDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-primary)] pb-16"
    >

      {/* ── Section Heading ── */}
      <div className="w-full text-center px-4 sm:px-6 md:px-8 lg:px-12 pt-16 pb-2 md:pb-4">
        <RichTextContent
          path="tabbedcallouts.section_heading"
          label="Section Heading"
          defaultValue='<p>Discover Your <span style="color:var(--vibe-color-accent)">New Home</span></p>'
          as="h2"
          className="font-heading text-4xl md:text-5xl font-normal text-primary-foreground leading-[1.1] uppercase"
        />
      </div>

      {/* ── Tab Bar ── */}
      <div className="bg-[var(--vibe-color-primary)] px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="cta-tab-bar relative flex" role="tablist">
          {/* Tab 1 */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'panel1'}
            onClick={() => setActiveTab('panel1')}
            className={`cta-tab-btn flex-1 py-6 px-4 font-heading font-normal tracking-widest uppercase transition-colors duration-300 text-sm md:text-base ${
              activeTab === 'panel1'
                ? 'text-primary-foreground opacity-100'
                : 'text-primary-foreground opacity-60 hover:opacity-90'
            }`}
          >
            <RichTextContent
              path="tabbedcallouts.panel1_label"
              label="Tab 1 Label"
              defaultValue="<p>Modern Living</p>"
              as="span"
            />
          </button>
          {/* Tab 2 */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'panel2'}
            onClick={() => setActiveTab('panel2')}
            className={`cta-tab-btn flex-1 py-6 px-4 font-heading font-normal tracking-widest uppercase transition-colors duration-300 text-sm md:text-base ${
              activeTab === 'panel2'
                ? 'text-primary-foreground opacity-100'
                : 'text-primary-foreground opacity-60 hover:opacity-90'
            }`}
          >
            <RichTextContent
              path="tabbedcallouts.panel2_label"
              label="Tab 2 Label"
              defaultValue="<p>Amenities</p>"
              as="span"
            />
          </button>
          {/* Tab 3 */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'panel3'}
            onClick={() => setActiveTab('panel3')}
            className={`cta-tab-btn flex-1 py-6 px-4 font-heading font-normal tracking-widest uppercase transition-colors duration-300 text-sm md:text-base ${
              activeTab === 'panel3'
                ? 'text-primary-foreground opacity-100'
                : 'text-primary-foreground opacity-60 hover:opacity-90'
            }`}
          >
            <RichTextContent
              path="tabbedcallouts.panel3_label"
              label="Tab 3 Label"
              defaultValue="<p>Prime Location</p>"
              as="span"
            />
          </button>

          {/* Baseline underline across all tabs (faint) */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--vibe-color-text-on-primary)] opacity-40"
          />
          {/* Sliding active-tab indicator */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 left-0 h-[3px] bg-[var(--vibe-color-text-on-primary)] transition-transform duration-300 ease-out"
            style={{
              width: `${100 / TAB_COUNT}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
          />
        </div>
      </div>

      {/* ── Panel 1: Tab 1 ── */}
      <div className={activeTab === 'panel1' ? 'flex flex-col md:flex-row h-auto md:min-h-[360px] lg:min-h-0 lg:h-[360px] mt-2 md:mt-12 px-4 sm:px-6 md:px-8 lg:px-12' : 'hidden'}>

        {/* Text */}
        <div className="md:w-3/5 flex flex-col justify-center px-10 py-6 md:px-12 md:py-10 lg:px-16 lg:py-16 bg-[var(--vibe-color-primary)]">
          <RichTextContent
            path="tabbedcallouts.panel1_heading"
            label="Tab 1 Heading"
            defaultValue="<p>Designed For Modern Luxury</p>"
            as="h2"
            className="font-heading text-2xl md:text-3xl font-normal text-primary-foreground leading-[1.15] mb-6 w-full text-center uppercase"
          />

          <RichTextContent
            path="tabbedcallouts.panel1_body"
            label="Tab 1 Body Text"
            defaultValue="<p>Thoughtfully designed residences in the heart of downtown — featuring premium finishes, open-concept layouts, and a community built around how you live today.</p>"
            as="div"
            className="font-body font-light text-[15px] text-primary-foreground opacity-70 leading-loose mb-10 w-full text-center"
          />

          <EditableSlotGroup groupPath="tabbedcallouts.panel1_cta" definition={panel1CtaDef} className="text-center">
            <a
              href={cta1.url || '/gallery'}
              target={cta1._target || '_self'}
              className="tabbed-cta group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta1.label || 'Browse Photos'}</span>
            </a>
          </EditableSlotGroup>
        </div>

        {/* Image — landscape */}
        <div className="md:w-2/5 relative min-h-[300px] md:min-h-0">
          <EditableSlotGroup
            groupPath="tabbedcallouts.panel1_image"
            definition={imgDef1}
            className="absolute inset-0"
          >
            {img1.image ? (
              <VibesitesImage
                url={img1.image}
                alt={img1.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* ── Panel 2: Tab 2 ── */}
      <div className={activeTab === 'panel2' ? 'flex flex-col md:flex-row h-auto md:min-h-[360px] lg:min-h-0 lg:h-[360px] mt-2 md:mt-12 px-4 sm:px-6 md:px-8 lg:px-12' : 'hidden'}>

        {/* Text */}
        <div className="md:w-3/5 flex flex-col justify-center px-10 py-6 md:px-12 md:py-10 lg:px-16 lg:py-16 bg-[var(--vibe-color-primary)]">
          <RichTextContent
            path="tabbedcallouts.panel2_heading"
            label="Tab 2 Heading"
            defaultValue="<p>Resort-Style Living, Every Day</p>"
            as="h2"
            className="font-heading text-2xl md:text-3xl font-normal text-primary-foreground leading-[1.15] mb-6 w-full text-center uppercase"
          />

          <RichTextContent
            path="tabbedcallouts.panel2_body"
            label="Tab 2 Body Text"
            defaultValue="<p>Every detail crafted for the way you live — from sunrise to sundown.</p>"
            as="div"
            className="font-body font-light text-[15px] text-primary-foreground opacity-70 leading-loose mb-10 w-full text-center"
          />

          <EditableSlotGroup groupPath="tabbedcallouts.panel2_cta" definition={panel2CtaDef} className="text-center">
            <a
              href={cta2.url || '/amenities'}
              target={cta2._target || '_self'}
              className="tabbed-cta group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta2.label || 'View All Amenities'}</span>
            </a>
          </EditableSlotGroup>
        </div>

        {/* Image — landscape */}
        <div className="md:w-2/5 relative min-h-[300px] md:min-h-0">
          <EditableSlotGroup
            groupPath="tabbedcallouts.panel2_image"
            definition={imgDef2}
            className="absolute inset-0"
          >
            {img2.image ? (
              <VibesitesImage
                url={img2.image}
                alt={img2.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* ── Panel 3: Tab 3 ── */}
      <div className={activeTab === 'panel3' ? 'flex flex-col md:flex-row h-auto md:min-h-[360px] lg:min-h-0 lg:h-[360px] mt-2 md:mt-12 px-4 sm:px-6 md:px-8 lg:px-12' : 'hidden'}>

        {/* Text */}
        <div className="md:w-3/5 flex flex-col justify-center px-10 py-6 md:px-12 md:py-10 lg:px-16 lg:py-16 bg-[var(--vibe-color-primary)]">
          <RichTextContent
            path="tabbedcallouts.panel3_heading"
            label="Tab 3 Heading"
            defaultValue="<p>Choose To Live Above It All</p>"
            as="h2"
            className="font-heading text-2xl md:text-3xl font-normal text-primary-foreground leading-[1.15] mb-6 w-full text-center uppercase"
          />

          <RichTextContent
            path="tabbedcallouts.panel3_body"
            label="Tab 3 Body Text"
            defaultValue="<p>Steps from parks, galleries, dining, and transit — live at the center of everything the city has to offer, without giving up the peace of home.</p>"
            as="div"
            className="font-body font-light text-[15px] text-primary-foreground opacity-70 leading-loose mb-10 w-full text-center"
          />

          <EditableSlotGroup groupPath="tabbedcallouts.panel3_cta" definition={panel3CtaDef} className="text-center">
            <a
              href={cta3.url || '/neighborhood'}
              target={cta3._target || '_self'}
              className="tabbed-cta group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta3.label || "See What's Nearby"}</span>
            </a>
          </EditableSlotGroup>
        </div>

        {/* Image — landscape */}
        <div className="md:w-2/5 relative min-h-[300px] md:min-h-0">
          <EditableSlotGroup
            groupPath="tabbedcallouts.panel3_image"
            definition={imgDef3}
            className="absolute inset-0"
          >
            {img3.image ? (
              <VibesitesImage
                url={img3.image}
                alt={img3.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Border, text, and hover wash all track --vibe-color-text-on-primary
           so the CTA always uses the property's textOnPrimary token against
           the primary brand background. */
        .tabbed-cta {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 75%, transparent);
          border-radius: 2px;
          color: var(--vibe-color-text-on-primary, #fff);
          box-shadow: none;
        }
        .tabbed-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 18%, transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .tabbed-cta:hover {
          border-color: var(--vibe-color-text-on-primary, #fff);
          color: var(--vibe-color-text-on-primary, #fff);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 18%, transparent);
        }
        .tabbed-cta:hover::before {
          opacity: 1;
        }
        .tabbed-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>

    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'TabbedCallouts',
  displayName: 'Tabbed Callouts',
  tokens: {
    // Section heading
    'tabbedcallouts.section_heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Discover Your <span style="color:var(--vibe-color-accent)">New Home</span></p>',
    },
    // Tab labels
    'tabbedcallouts.panel1_label': {
      label: 'Tab 1 Label',
      type: 'richtext',
      defaultValue: '<p>Modern Living</p>',
    },
    'tabbedcallouts.panel2_label': {
      label: 'Tab 2 Label',
      type: 'richtext',
      defaultValue: '<p>Amenities</p>',
    },
    'tabbedcallouts.panel3_label': {
      label: 'Tab 3 Label',
      type: 'richtext',
      defaultValue: '<p>Prime Location</p>',
    },
    // Panel 1 — Tab 1
    'tabbedcallouts.panel1_heading': {
      label: 'Tab 1 Heading',
      type: 'richtext',
      defaultValue: '<p>Designed For Modern Luxury</p>',
    },
    'tabbedcallouts.panel1_body': {
      label: 'Tab 1 Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Thoughtfully designed residences in the heart of downtown — featuring premium finishes, open-concept layouts, and a community built around how you live today.</p>',
    },
    // Panel 2 — Tab 2
    'tabbedcallouts.panel2_heading': {
      label: 'Tab 2 Heading',
      type: 'richtext',
      defaultValue: '<p>Resort-Style Living, Every Day</p>',
    },
    'tabbedcallouts.panel2_body': {
      label: 'Tab 2 Body Text',
      type: 'richtext',
      defaultValue: '<p>Every detail crafted for the way you live — from sunrise to sundown.</p>',
    },
    // Panel 3 — Tab 3
    'tabbedcallouts.panel3_heading': {
      label: 'Tab 3 Heading',
      type: 'richtext',
      defaultValue: '<p>Choose To Live Above It All</p>',
    },
    'tabbedcallouts.panel3_body': {
      label: 'Tab 3 Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Steps from parks, galleries, dining, and transit — live at the center of everything the city has to offer, without giving up the peace of home.</p>',
    },
  },
  slotGroups: {
    'tabbedcallouts.panel1_image': panel1ImageDef,
    'tabbedcallouts.panel1_cta': panel1CtaDef,
    'tabbedcallouts.panel2_image': panel2ImageDef,
    'tabbedcallouts.panel2_cta': panel2CtaDef,
    'tabbedcallouts.panel3_image': panel3ImageDef,
    'tabbedcallouts.panel3_cta': panel3CtaDef,
  },
};

(TabbedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TabbedCallouts;
